import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaUser, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { RiPassportLine } from "react-icons/ri";
import { FaBook, FaPuzzlePiece } from 'react-icons/fa';
import { useReferralFromURL } from '../hooks/useReferralFromURL';

const LandingPage = () => {

  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(null); 

  const { referralCode, isFromURL } = useReferralFromURL();
  const [showReferralBanner, setShowReferralBanner] = useState(true);

  const [showScrollTop, setShowScrollTop] = useState(false);

  const [waitlistEmail, setWaitlistEmail] = useState('');
const [isSubmittingWaitlist, setIsSubmittingWaitlist] = useState(false);
const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
const [waitlistError, setWaitlistError] = useState('');
  
  useEffect(() => {

    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    

    window.addEventListener('scroll', handleScroll);
    

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
  if (isFromURL && referralCode && showReferralBanner) {
    const timer = setTimeout(() => {
      setShowReferralBanner(false);
    }, 4000); 

    return () => clearTimeout(timer);
  }
}, [isFromURL, referralCode, showReferralBanner]);
  

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleWaitlistSubmit = async () => {
  if (!waitlistEmail.trim()) {
    setWaitlistError('Please enter a valid email address');
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(waitlistEmail.trim())) {
    setWaitlistError('Please enter a valid email address');
    return;
  }

  setIsSubmittingWaitlist(true);
  setWaitlistError('');

  try {
    const response = await fetch('https://tinker.0xretro.dev/public/api/v1/waitlist.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: waitlistEmail.trim()
      })
    });

    if (response.ok) {
      setWaitlistSubmitted(true);
      setWaitlistEmail('');
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setWaitlistSubmitted(false);
      }, 5000);
    } else {
      const errorData = await response.json().catch(() => ({}));
      setWaitlistError(errorData.message || 'Failed to join waitlist. Please try again.');
    }
  } catch (error) {
    console.error('Waitlist submission error:', error);
    setWaitlistError('Network error. Please check your connection and try again.');
  } finally {
    setIsSubmittingWaitlist(false);
  }
};

// Reset error when user starts typing
const handleEmailChange = (e) => {
  setWaitlistEmail(e.target.value);
  if (waitlistError) {
    setWaitlistError('');
  }
};


  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

 
const faqData = [
  {
    question: "Why don't you store my social media usernames?",
    answer: "For the same reason banks don't store your password in plain text. We use cryptographic proofs that verify platform ownership without exposing your personal information. This protects you from data breaches, identity theft, and privacy violations while still proving you control these accounts.",
    category: "privacy"
  },
  {
    question: "Is this a scam because you don't store my data?",
    answer: "Not storing your data is actually a feature, not a bug! Traditional services store your information, making you vulnerable to breaches and data misuse. Our cryptographic approach provides stronger verification while protecting your privacy. It's like having a mathematical signature that proves ownership without revealing the signature itself.",
    category: "trust"
  },
  {
    question: "What exactly do you store on the blockchain?",
    answer: "We store: your wallet address (already public), which platforms you've verified (like 'Twitter' or 'GitHub'), cryptographic proof hashes, and verification timestamps. We DON'T store: your actual usernames, emails, profile pictures, or any personal information. Just proof that you verified each platform.",
    category: "privacy"
  },
  {
    question: "How do I know the verification actually worked?",
    answer: "Every verification is recorded on the blockchain with a timestamp and cryptographic proof hash. You can independently verify this by checking your passport on-chain. The proof is mathematically impossible to forge and provides stronger verification than storing raw usernames.",
    category: "verification"
  },
  {
    question: "What happens if Passly gets hacked?",
    answer: "Even if our entire database is compromised, hackers would only find cryptographic hashes and platform names - not your personal information. Your social media handles, emails, and profile data remain completely safe because we never store them in the first place.",
    category: "security"
  },
  {
    question: "How do dApps verify my identity?",
    answer: "dApps can check your passport on-chain to see which platforms you've verified (Twitter, GitHub, etc.) without ever seeing your actual usernames. They get proof of verification without compromising your privacy. It's verification without revelation.",
    category: "usage"
  },
  {
    question: "Can I trust that my verification is permanent?",
    answer: "Yes! Your verification is recorded on the blockchain, making it immutable and permanent. Even if Passly disappears tomorrow, your verifications remain valid and can be independently verified by anyone with access to the blockchain.",
    category: "trust"
  },
  {
    question: "Why should I use this instead of other identity services?",
    answer: "Most identity services require you to trust them with your data. Passly is trustless - you don't need to trust us because the verification is cryptographically provable and stored on an immutable blockchain. Plus, your personal data stays private by design.",
    category: "comparison"
  },
{
  question: "What does the Passly browser extension do?",
  answer: "The extension shows you which users on supported platforms have verified their identity through Passly's blockchain technology. You'll see animated verification badges on profiles of users who have proven they're real, helping you avoid scams, bots, and impersonators.",
  category: "extension"
},
{
  question: "How does the extension know who's verified?",
  answer: "It checks each profile against the blockchain where verified users store their digital passports. When you visit a Twitter/X profile, for example, it instantly looks up whether that user has blockchain-verified their identity - all using public blockchain data.",
  category: "extension"
},
{
  question: "Does the extension collect my browsing data?",
  answer: "No! The extension only accesses public blockchain data to check verification status. It doesn't collect, store, or transmit any of your personal information, browsing history, or account details. Your privacy is completely protected.",
  category: "extension"
},
{
  question: "Which platforms does the extension support?",
  answer: "Currently supports Twitter/X with more platforms being added regularly. The extension automatically detects when you're on supported platforms and shows verification badges for users who have verified their identity through Passly.",
  category: "extension"
},
{
  question: "Will the extension slow down my browsing?",
  answer: "Not at all! The extension is lightweight and only makes quick blockchain queries when you visit supported platform profiles. It's designed to be fast and unobtrusive - you'll barely notice it's running except for the helpful verification badges.",
  category: "extension"
},
{
  question: "Do I need a Passly passport to use the extension?",
  answer: "No, the extension works for anyone! You can use it to see who's verified even if you don't have your own Passly passport yet. However, having a passport means others with the extension will see you as verified too.",
  category: "extension"
},
{
  question: "Can others see that I'm using the Passly extension?",
  answer: "No, using the extension is completely private. Others can't see that you have it installed. The only thing that's visible is if you have a verified Passly passport - then others with the extension will see your verification badge.",
  category: "extension"
}
];

  return (
    <div className="landing-page">
<div className="landing-header">
  <div className="container landing-header-container">
    <div className="landing-logo-container">
      <div className="landing-logo">PASSLY</div>
      <span className="landing-header-beta-tag">BETA</span>
    </div>
    <div className="landing-buttons">
      <Link to="/app" className="landing-cta-button landing-header-button">
        <FaUser style={{ marginRight: '8px' }} />
        My Passport
      </Link>
      <Link to="/docs" className="landing-dev-button landing-header-button">
        <FaBook style={{ marginRight: '8px' }} />
        Developers
      </Link>
    </div>
  </div>
</div>

 {isFromURL && referralCode && showReferralBanner && (
  <div style={{
    background: 'linear-gradient(135deg, rgba(15, 15, 35, 0.9) 0%, rgba(26, 26, 46, 0.95) 50%, rgba(22, 33, 62, 0.9) 100%)',
    borderBottom: '1px solid rgba(124, 77, 255, 0.3)',
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
    padding: '1rem 0',
    textAlign: 'center',
    position: 'sticky',
    top: '80px',
    zIndex: 100,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 20px rgba(124, 77, 255, 0.1)',
    animation: 'slideIn 0.5s ease-out',
    position: 'relative',
    overflow: 'hidden'
  }}>
    {/* Subtle animated background */}
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(45deg, rgba(124, 77, 255, 0.05) 0%, rgba(0, 204, 184, 0.05) 100%)',
      animation: 'pulse 3s ease-in-out infinite'
    }}></div>

    <div className="container" style={{ position: 'relative', zIndex: 2 }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.5rem',
        flexWrap: 'wrap',
        position: 'relative'
      }}>
        <span style={{
          fontSize: '1.8rem',
          animation: 'bounce 2s ease-in-out infinite',
          filter: 'drop-shadow(0 0 8px rgba(124, 77, 255, 0.4))'
        }}>🎉</span>
        
        <div style={{ flex: 1, maxWidth: '500px' }}>
          <div style={{
            background: 'linear-gradient(135deg, #7c4dff 0%, #00ccb8 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: '700',
            fontSize: '1.2rem',
            marginBottom: '0.25rem',
            letterSpacing: '0.02em'
          }}>
            You've been invited to join Passly!
          </div>
          <div style={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '0.95rem',
            lineHeight: '1.4'
          }}>
            Referral code{' '}
            <span style={{
              color: '#ffffff',
              padding: '0.2rem 0.6rem',
              background: 'linear-gradient(135deg, rgba(124, 77, 255, 0.25) 0%, rgba(0, 204, 184, 0.25) 100%)',
              borderRadius: '6px',
              fontFamily: 'Monaco',
              fontSize: '0.8rem',
              letterSpacing: '1px',
              border: '1px solid rgba(124, 77, 255, 0.3)',
              boxShadow: '0 2px 10px rgba(124, 77, 255, 0.15)'
            }}>
              {referralCode}
            </span>{' '}
            will be applied for your passport.
          </div>
        </div>
        
        <span style={{
          fontSize: '1.8rem',
          animation: 'sparkle 2s ease-in-out infinite',
          filter: 'drop-shadow(0 0 8px rgba(0, 204, 184, 0.4))'
        }}>✨</span>
      </div>
    </div>

    {/* Progress bar */}
    <div style={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      height: '2px',
      background: 'linear-gradient(90deg, #7c4dff 0%, #00ccb8 100%)',
      animation: 'progressBar 4s linear forwards',
      transformOrigin: 'left'
    }}></div>

    <style jsx>{`
      @keyframes slideIn {
        from {
          transform: translateY(-100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
      
      @keyframes bounce {
        0%, 20%, 50%, 80%, 100% {
          transform: translateY(0);
        }
        40% {
          transform: translateY(-6px);
        }
        60% {
          transform: translateY(-3px);
        }
      }
      
      @keyframes sparkle {
        0%, 100% {
          transform: scale(1) rotate(0deg);
          opacity: 1;
        }
        50% {
          transform: scale(1.1) rotate(180deg);
          opacity: 0.8;
        }
      }
      
      @keyframes pulse {
        0%, 100% {
          opacity: 0.3;
        }
        50% {
          opacity: 0.6;
        }
      }
      
      @keyframes progressBar {
        from {
          width: 100%;
        }
        to {
          width: 0%;
        }
      }
    `}</style>
  </div>
)}
      <div className="landing-hero">
        <div className="container">
          <div className="landing-hero-content">
            <h1 className="landing-title">Decentralized Identity for the Web3 World</h1>
            <p className="landing-subtitle">
              Verify and prove your social identity on the blockchain without compromising your privacy.
            </p>
            <div className="landing-cta">
              <Link to="/app" className="landing-cta-button">
              <RiPassportLine style={{ marginRight: '8px', fontSize: '20px' }} />
                Get Your Passport
              </Link>
  <a 
    href="https://chromewebstore.google.com/detail/passly/pmgiieimnfeafhmmglhnmffmkbkhfegc" 
    target="_blank" 
    rel="noopener noreferrer"
    className="landing-secondary-button"
  >
    <FaPuzzlePiece style={{ marginRight: '8px' }} />
    Install Extension
  </a>
            </div>
          </div>
          <div className="landing-hero-image">
            <div className="passport-visual">
              <div className="passport-card">
                <div className="passport-header">
                  <div className="passport-logo">Social Passport</div>
                  <div className="passport-chip"></div>
                </div>
                <div className="passport-data">
                  <div className="passport-verification twitter-verified">
                    <div className="verification-icon"></div>
                    <div className="verification-label">Twitter Verified</div>
                  </div>
                  <div className="passport-verification discord-verified">
                    <div className="verification-icon"></div>
                    <div className="verification-label">Discord Verified</div>
                  </div>
                  <div className="passport-verification github-verified">
                    <div className="verification-icon"></div>
                    <div className="verification-label">GitHub Verified</div>
                  </div>
                </div>
                <div className="passport-footer">
                  <div className="passport-address"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="learn-more" className="enhanced-features-section">
        <div className="container">
          <h2 className="enhanced-section-title">
            Why Use <span className="text-gradient">Passly</span>?
          </h2>

          <div className="enhanced-feature-grid">
            <div 
              className={`enhanced-feature-card ${hoveredFeature === 'privacy' ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredFeature('privacy')}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div className="enhanced-feature-icon privacy-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <div className="enhanced-icon-glow"></div>
              </div>
              <h3 className="enhanced-feature-title">Privacy First</h3>
              <p className="enhanced-feature-desc">
                Prove your identity without revealing personal information and preserving your privacy.
              </p>
              <div className="enhanced-card-shine"></div>
            </div>

            <div 
              className={`enhanced-feature-card ${hoveredFeature === 'sovereign' ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredFeature('sovereign')}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div className="enhanced-feature-icon ownership-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <div className="enhanced-icon-glow"></div>
              </div>
              <h3 className="enhanced-feature-title">Self-Sovereign</h3>
              <p className="enhanced-feature-desc">
                Your identity belongs to you. No central authority can revoke or control your digital passport.
              </p>
              <div className="enhanced-card-shine"></div>
            </div>

            <div 
              className={`enhanced-feature-card ${hoveredFeature === 'interop' ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredFeature('interop')}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div className="enhanced-feature-icon interop-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
                </svg>
                <div className="enhanced-icon-glow"></div>
              </div>
              <h3 className="enhanced-feature-title">Interoperable</h3>
              <p className="enhanced-feature-desc">
                Use your verified identity across multiple dApps and platforms in the blockchain ecosystem.
              </p>
              <div className="enhanced-card-shine"></div>
            </div>

            <div 
              className={`enhanced-feature-card ${hoveredFeature === 'secure' ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredFeature('secure')}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div className="enhanced-feature-icon secure-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
                <div className="enhanced-icon-glow"></div>
              </div>
              <h3 className="enhanced-feature-title">Secure</h3>
              <p className="enhanced-feature-desc">
                Built on battle-tested blockchain technology with robust security and verification protocols.
              </p>
              <div className="enhanced-card-shine"></div>
            </div>
          </div>
        </div>
        <div className="enhanced-bg-gradient gradient-left"></div>
        <div className="enhanced-bg-gradient gradient-right"></div>
      </div>


      <div className="enhanced-workflow-section">
        <div className="container">
          <h2 className="enhanced-section-title">
            How It <span className="text-gradient">Works</span>
          </h2>
          
          <div className="enhanced-steps-container">
            <div 
              className={`enhanced-step-item ${hoveredStep === 1 ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredStep(1)}
              onMouseLeave={() => setHoveredStep(null)}
            >
              <div className="enhanced-step-number-container">
                <div className="enhanced-step-number">1</div>
                <div className="enhanced-step-glow"></div>
              </div>
              <h3 className="enhanced-step-title">Connect Wallet</h3>
              <p className="enhanced-step-desc">
                Connect your Web3 wallet to create your unique digital passport.
              </p>
            </div>

            <div className="enhanced-step-connector">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14m-4-4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <div 
              className={`enhanced-step-item ${hoveredStep === 2 ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredStep(2)}
              onMouseLeave={() => setHoveredStep(null)}
            >
              <div className="enhanced-step-number-container">
                <div className="enhanced-step-number">2</div>
                <div className="enhanced-step-glow"></div>
              </div>
              <h3 className="enhanced-step-title">Verify Accounts</h3>
              <p className="enhanced-step-desc">
                Authenticate your social media accounts through secure OAuth flows.
              </p>
            </div>

            <div className="enhanced-step-connector">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14m-4-4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <div 
              className={`enhanced-step-item ${hoveredStep === 3 ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredStep(3)}
              onMouseLeave={() => setHoveredStep(null)}
            >
              <div className="enhanced-step-number-container">
                <div className="enhanced-step-number">3</div>
                <div className="enhanced-step-glow"></div>
              </div>
              <h3 className="enhanced-step-title">On-Chain Verification</h3>
              <p className="enhanced-step-desc">
                Your verifications are recorded on-chain as tamper-proof credentials.
              </p>
            </div>

            <div className="enhanced-step-connector">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14m-4-4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <div 
              className={`enhanced-step-item ${hoveredStep === 4 ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredStep(4)}
              onMouseLeave={() => setHoveredStep(null)}
            >
              <div className="enhanced-step-number-container">
                <div className="enhanced-step-number">4</div>
                <div className="enhanced-step-glow"></div>
              </div>
              <h3 className="enhanced-step-title">Use Anywhere</h3>
              <p className="enhanced-step-desc">
                Leverage your verified identity across web3 with a single click.
              </p>
            </div>
          </div>
        </div>
        <div className="enhanced-workflow-bg"></div>
      </div>

      {/* NFC Physical ID Section */}
<div className="nfc-section">
  <div className="container">
    <div className="nfc-content">
      <div className="nfc-text-content">
        <div className="nfc-badge">
          <span className="nfc-badge-icon">🚀</span>
          <span className="nfc-badge-text">Coming Soon</span>
        </div>
        
        <h2 className="nfc-title">
          Bridge Digital & <span className="text-gradient">Physical</span> Identity
        </h2>
        
        <p className="nfc-subtitle">
          Your Passly verification will soon extend beyond the digital realm. Tap, verify, and authenticate in the real world with NFC-enabled physical ID cards.
        </p>
        
        <div className="nfc-features">
          <div className="nfc-feature-item">
            <div className="nfc-feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div className="nfc-feature-text">
              <h4>Instant Verification</h4>
              <p>Tap your card to instantly prove your digital identity at events, venues, or services</p>
            </div>
          </div>
          
          <div className="nfc-feature-item">
            <div className="nfc-feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <div className="nfc-feature-text">
              <h4>Secure & Private</h4>
              <p>Same cryptographic proofs, now in a physical form factor that works offline</p>
            </div>
          </div>
          
          <div className="nfc-feature-item">
            <div className="nfc-feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"/>
              </svg>
            </div>
            <div className="nfc-feature-text">
              <h4>Universal Access</h4>
              <p>Bridge Web3 and traditional systems with one unified identity solution</p>
            </div>
          </div>
        </div>
        
        <div className="nfc-cta">
          <div className="nfc-waitlist-form">
            <div className="nfc-email-input-container">
              <input
                type="email"
                placeholder="Enter your email for early access"
                className="nfc-email-input"
                value={waitlistEmail}
                onChange={(e) => setWaitlistEmail(e.target.value)}
                disabled={isSubmittingWaitlist}
              />
              <button 
                className="nfc-notify-button"
                onClick={handleWaitlistSubmit}
                disabled={isSubmittingWaitlist || !waitlistEmail.trim()}
              >
                {isSubmittingWaitlist ? (
                  <>
                    <span className="nfc-loading-spinner"></span>
                    <span>Joining...</span>
                  </>
                ) : waitlistSubmitted ? (
                  <>
                    <span className="nfc-success-icon">✓</span>
                    <span>Added!</span>
                  </>
                ) : (
                  <>
                    <span className="nfc-notify-icon">🔔</span>
                    <span>Join Waitlist</span>
                  </>
                )}
              </button>
            </div>
            {waitlistError && (
              <p className="nfc-error-message">{waitlistError}</p>
            )}
            {waitlistSubmitted && !waitlistError && (
              <p className="nfc-success-message">
                🎉 You're on the list! We'll notify you when physical cards are available.
              </p>
            )}
          </div>
          <p className="nfc-note">Join the waitlist for early access to physical Passly cards</p>
        </div>
      </div>
      
      <div className="nfc-visual-content">
        <div className="nfc-card-showcase">
          <div className="nfc-card">
            <div className="nfc-card-inner">
              <div className="nfc-card-front">
                <div className="nfc-card-header">
                  <div className="nfc-logo">PASSLY</div>
                </div>
                
                <div className="nfc-card-content">
                  <div className="nfc-verification-dots">
                    <div className="nfc-dot twitter"></div>
                    <div className="nfc-dot github"></div>
                    <div className="nfc-dot discord"></div>
                    <div className="nfc-dot solana"></div>
                  </div>
                  
                  <div className="nfc-card-address">
                    <div className="nfc-address-label">Wallet</div>
                    <div className="nfc-address-value">0x1234...5678</div>
                  </div>
                </div>
                
                <div className="nfc-card-footer">
                  <div className="nfc-footer-left">
                  </div>
                  <div className="nfc-footer-right">
                    <div className="nfc-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                        <path d="M2 17l10 5 10-5"/>
                        <path d="M2 12l10 5 10-5"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="nfc-interaction-visual">
            <div className="nfc-tap-animation">
              <div className="nfc-hand">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0"/>
                  <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2"/>
                  <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8"/>
                  <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/>
                </svg>
              </div>
              <div className="nfc-waves">
                <div className="nfc-wave nfc-wave-1"></div>
                <div className="nfc-wave nfc-wave-2"></div>
                <div className="nfc-wave nfc-wave-3"></div>
              </div>
            </div>
            
            <div className="nfc-reader">
              <div className="nfc-reader-screen">
                <div className="nfc-status verified">
                  <div className="nfc-status-content">
                    <div className="nfc-check-icon">✓</div>
                    <div className="nfc-status-text">VERIFIED</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div className="nfc-bg-gradient"></div>
</div>


      {/* FAQ Section*/}
      <div className="faq-section">
        <div className="container">
          <h2 className="enhanced-section-title">
            Frequently Asked <span className="text-gradient">Questions</span>
          </h2>
          <p className="faq-subtitle">
            Common questions about privacy, security, and how Passly protects your identity
          </p>
          
          <div className="faq-container">
            {faqData.map((faq, index) => (
              <div key={index} className={`faq-item ${openFAQ === index ? 'open' : ''}`}>
                <button 
                  className="faq-question"
                  onClick={() => toggleFAQ(index)}
                  aria-expanded={openFAQ === index}
                >
                  <span className="faq-question-text">{faq.question}</span>
                  <div className="faq-icon">
                    {openFAQ === index ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                </button>
                <div className={`faq-answer ${openFAQ === index ? 'open' : ''}`}>
                  <div className="faq-answer-content">
                    <p>{faq.answer}</p>
                    <div className={`faq-category ${faq.category}`}>
                      {faq.category}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="faq-footer">
            <div className="faq-privacy-note">
              <div className="privacy-icon">🔒</div>
              <div>
                <h4>Privacy by Design</h4>
                <p>Blockchain-based proofs eliminate the need to store your personal information.</p>
              </div>
            </div>
          </div>
        </div>
      </div>


      <div className="enhanced-use-cases-section">
        <div className="container">
          <h2 className="enhanced-section-title">
            Passly Use <span className="text-gradient">Cases</span>
          </h2>
          
          <div className="enhanced-use-cases-grid">
            <div className="enhanced-use-case-card">
              <div className="enhanced-use-case-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l-3 5h6l-3 5h6l-3 5h6l-3 5"/>
                  <path d="M12 2l3 5h-6l3 5h-6l3 5h-6l3 5"/>
                </svg>
                <div className="enhanced-icon-glow"></div>
              </div>
              <h3 className="enhanced-use-case-title">DeFi Access</h3>
              <p className="enhanced-use-case-desc">
                Get preferential access to DeFi protocols that require verified social presence.
              </p>
              <div className="enhanced-card-shine"></div>
            </div>

            <div className="enhanced-use-case-card">
              <div className="enhanced-use-case-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <div className="enhanced-icon-glow"></div>
              </div>
              <h3 className="enhanced-use-case-title">DAO Participation</h3>
              <p className="enhanced-use-case-desc">
                Join DAOs with confidence that all members have verified identities, reducing sybil attacks.
              </p>
              <div className="enhanced-card-shine"></div>
            </div>

            <div className="enhanced-use-case-card">
              <div className="enhanced-use-case-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <path d="M2 17l10 5 10-5"></path>
                  <path d="M2 12l10 5 10-5"></path>
                </svg>
                <div className="enhanced-icon-glow"></div>
              </div>
              <h3 className="enhanced-use-case-title">Web3 Reputation</h3>
              <p className="enhanced-use-case-desc">
                Build reputation across platforms without starting from scratch on each new service.
              </p>
              <div className="enhanced-card-shine"></div>
            </div>

            <div className="enhanced-use-case-card">
              <div className="enhanced-use-case-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
                <div className="enhanced-icon-glow"></div>
              </div>
              <h3 className="enhanced-use-case-title">Sybil Resistance</h3>
              <p className="enhanced-use-case-desc">
                Participate in airdrops and token distributions with verified unique identity.
              </p>
              <div className="enhanced-card-shine"></div>
            </div>
          </div>
        </div>
        <div className="enhanced-use-cases-bg-gradient"></div>
      </div>


      <div className="enhanced-cta-section">
        <div className="container">
          <div className="enhanced-cta-content">
            <h2 className="enhanced-cta-title">
              Ready to Take Control of <span className="text-gradient">Your Digital Identity</span>?
            </h2>
            <p className="enhanced-cta-desc">
              Create your Social Passport now and join the decentralized identity revolution.
            </p>
            <Link to="/app" className="enhanced-cta-button">
               <RiPassportLine style={{ fontSize: '20px' }} />
              Get Your Passport
              <svg className="button-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>
        </div>
        <div className="enhanced-cta-bg-gradient"></div>
      </div>

      <footer className="enhanced-footer">
        <div className="container">
          <div className="enhanced-footer-content">
            <div className="footer-logo">
              <span className="text-gradient">PASSLY</span>
            </div>
            <div className="enhanced-footer-links">
              <Link to="/privacy" className="enhanced-footer-link">Privacy Policy</Link>
              <Link to="/terms" className="enhanced-footer-link">Terms of Service</Link>
            </div>
          </div>
          <div className="enhanced-footer-divider"></div>
          <div className="enhanced-footer-bottom">
            <div className="enhanced-copyright">
              © {new Date().getFullYear()} Passly Protocol. All rights reserved.
            </div>
            <div className="enhanced-social-links">
              <a href="https://x.com/PasslyAI" target="_blank" className="enhanced-social-link" aria-label="Twitter">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>


      <button 
        className={`return-to-top ${showScrollTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Return to top"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 19V5M5 12l7-7 7 7"/>
        </svg>
      </button>


      <style jsx>{`
        /* Enhanced feature section styles */
        .enhanced-features-section {
          background-color: var(--color-background-secondary);
          position: relative;
          overflow: hidden;
          padding: 6rem 0;
          box-shadow: 0 -1px 0 rgba(255, 255, 255, 0.05), 0 1px 0 rgba(255, 255, 255, 0.05);
        }

        .enhanced-section-title {
          font-size: 2.5rem;
          font-weight: 800;
          text-align: center;
          margin-bottom: 4rem;
          color: var(--color-text-primary);
          position: relative;
        }

        .text-gradient {
          background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          text-fill-color: transparent;
        }

        .enhanced-feature-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2.5rem;
          position: relative;
          z-index: 2;
        }

        .enhanced-feature-card {
          background: linear-gradient(145deg, rgba(40, 40, 40, 0.4), rgba(30, 30, 30, 0.8));
          border-radius: 16px;
          padding: 2.5rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.05);
          box-shadow: 
            0 10px 20px rgba(0, 0, 0, 0.1),
            0 6px 6px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }

        .enhanced-feature-card.hovered {
          transform: translateY(-10px);
          box-shadow: 
            0 20px 40px rgba(0, 0, 0, 0.2),
            0 12px 12px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .enhanced-feature-icon {
          width: 70px;
          height: 70px;
          border-radius: 20px;
          background: linear-gradient(145deg, rgba(50, 50, 50, 0.6), rgba(30, 30, 30, 0.8));
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
          transition: all 0.3s ease;
          position: relative;
          box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);
        }

        .enhanced-feature-card.hovered .enhanced-feature-icon {
          transform: scale(1.1);
        }

        .enhanced-feature-icon svg {
          width: 35px;
          height: 35px;
          color: var(--color-primary);
          transition: all 0.3s ease;
          position: relative;
          z-index: 1;
        }

        .enhanced-feature-card.hovered .enhanced-feature-icon svg {
          color: var(--color-secondary);
        }

        .enhanced-icon-glow {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 20px;
          background: radial-gradient(circle, rgba(124, 77, 255, 0.3) 0%, rgba(0, 204, 184, 0.1) 70%, transparent 100%);
          opacity: 0.3;
          transition: opacity 0.3s ease;
        }

        .enhanced-feature-card.hovered .enhanced-icon-glow {
          opacity: 0.8;
        }

        .enhanced-feature-title {
          font-size: 1.4rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: var(--color-text-primary);
          position: relative;
        }

        .enhanced-feature-title::after {
          content: '';
          position: absolute;
          bottom: -10px;
          left: 50%;
          transform: translateX(-50%);
          width: 40px;
          height: 2px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
          border-radius: 2px;
        }

        .enhanced-feature-desc {
          color: var(--color-text-secondary);
          line-height: 1.7;
          margin-top: 1.2rem;
          font-size: 1rem;
        }

        .enhanced-card-shine {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.05) 50%,
            rgba(255, 255, 255, 0) 100%
          );
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .enhanced-feature-card.hovered .enhanced-card-shine {
          opacity: 1;
        }

        .enhanced-bg-gradient {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          z-index: 1;
        }

        .gradient-left {
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(124, 77, 255, 0.15) 0%, transparent 70%);
          top: -100px;
          left: -100px;
        }

        .gradient-right {
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(0, 204, 184, 0.15) 0%, transparent 70%);
          bottom: -200px;
          right: -100px;
        }

        /* Enhanced workflow section styles */
        .enhanced-workflow-section {
          background-color: var(--color-background);
          position: relative;
          padding: 6rem 0;
          overflow: hidden;
        }

        .enhanced-workflow-bg {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(900px circle at 70% 20%, rgba(124, 77, 255, 0.07) 0%, transparent 60%),
            radial-gradient(800px circle at 30% 70%, rgba(0, 204, 184, 0.07) 0%, transparent 60%);
          z-index: 1;
        }

        .enhanced-steps-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 4rem;
          position: relative;
          z-index: 2;
        }

        .enhanced-step-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 0 1rem;
          position: relative;
          transition: all 0.3s ease;
        }

        .enhanced-step-item.hovered {
          transform: translateY(-8px);
        }

        .enhanced-step-number-container {
          position: relative;
          width: 60px;
          height: 60px;
          margin-bottom: 1.5rem;
        }

        .enhanced-step-number {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(124, 77, 255, 0.6) 0%, rgba(0, 204, 184, 0.6) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: bold;
          color: #ffffff;
          transition: all 0.3s ease;
          position: relative;
          z-index: 1;
          box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);
        }

        .enhanced-step-item.hovered .enhanced-step-number {
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
          transform: scale(1.1);
        }

        .enhanced-step-glow {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(124, 77, 255, 0.4) 0%, rgba(0, 204, 184, 0.2) 70%, transparent 100%);
          filter: blur(8px);
          opacity: 0.3;
          transition: opacity 0.3s ease;
        }

        .enhanced-step-item.hovered .enhanced-step-glow {
          opacity: 0.8;
        }

        .enhanced-step-title {
          font-size: 1.25rem;
          font-weight: 700;
          margin-bottom: 0.75rem;
          color: var(--color-text-primary);
        }

        .enhanced-step-desc {
          color: var(--color-text-secondary);
          line-height: 1.6;
          font-size: 0.95rem;
          max-width: 200px;
          margin: 0 auto;
        }

        .enhanced-step-connector {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 60px;
          color: rgba(124, 77, 255, 0.4);
          flex-shrink: 0;
          height: 24px;
          transition: all 0.3s ease;
        }

        /* FAQ Section Styles - NEW */
        .faq-section {
          background: linear-gradient(135deg, rgba(15, 15, 35, 0.95) 0%, rgba(26, 26, 46, 0.95) 50%, rgba(22, 33, 62, 0.95) 100%);
          padding: 6rem 0;
          position: relative;
          overflow: hidden;
        }

        .faq-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, 
            transparent, 
            rgba(124, 77, 255, 0.3), 
            rgba(0, 204, 184, 0.3), 
            transparent
          );
        }

        .faq-subtitle {
          text-align: center;
          color: var(--color-text-secondary);
          font-size: 1.1rem;
          margin-bottom: 3rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.6;
        }

        .faq-container {
          max-width: 900px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .faq-item {
          background: linear-gradient(145deg, rgba(30, 30, 30, 0.6), rgba(20, 20, 20, 0.8));
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.05);
          overflow: hidden;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }

        .faq-item:hover {
          border-color: rgba(124, 77, 255, 0.3);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
        }

        .faq-item.open {
          border-color: rgba(124, 77, 255, 0.4);
          box-shadow: 0 10px 30px rgba(124, 77, 255, 0.1);
        }

        .faq-question {
          width: 100%;
          padding: 1.5rem 2rem;
          background: none;
          border: none;
          text-align: left;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: all 0.3s ease;
          color: var(--color-text-primary);
        }

        .faq-question:hover {
          background: rgba(255, 255, 255, 0.02);
        }

        .faq-question-text {
          font-size: 1.1rem;
          font-weight: 600;
          line-height: 1.4;
          flex: 1;
          margin-right: 1rem;
        }

        .faq-icon {
          color: var(--color-primary);
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
        }

        .faq-item.open .faq-icon {
          color: var(--color-secondary);
        }

        .faq-answer {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease;
        }

        .faq-answer.open {
          max-height: 500px;
        }

        .faq-answer-content {
          padding: 0 2rem 1.5rem 2rem;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
          margin-top: -1px;
        }

        .faq-answer-content p {
          color: var(--color-text-secondary);
          line-height: 1.7;
          margin: 1rem 0;
          font-size: 1rem;
        }

        .faq-category {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-top: 1rem;
        }

        .faq-category.privacy {
          background: rgba(124, 77, 255, 0.2);
          color: var(--color-primary);
          border: 1px solid rgba(124, 77, 255, 0.3);
        }

        .faq-category.verification {
          background: rgba(0, 204, 184, 0.2);
          color: var(--color-secondary);
          border: 1px solid rgba(0, 204, 184, 0.3);
        }

        .faq-category.trust {
          background: rgba(255, 215, 0, 0.2);
          color: #FFD700;
          border: 1px solid rgba(255, 215, 0, 0.3);
        }

        .faq-category.security {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
          border: 1px solid rgba(34, 197, 94, 0.3);
        }

        .faq-category.extension {
          background: rgba(5, 76, 88, 0.97);
          color:rgb(19, 229, 236);
          border: 1px solid rgba(34, 197, 94, 0.3);
        }

        .faq-category.comparison {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
          border: 1px solid rgba(239, 68, 68, 0.3);
        }

        .faq-category.usage {
          background: rgba(168, 85, 247, 0.2);
          color: #a855f7;
          border: 1px solid rgba(168, 85, 247, 0.3);
        }

        .faq-footer {
          margin-top: 4rem;
          text-align: center;
        }

        .faq-privacy-note {
          display: inline-flex;
          align-items: center;
          gap: 1rem;
          background: linear-gradient(135deg, rgba(124, 77, 255, 0.1) 0%, rgba(0, 204, 184, 0.1) 100%);
          padding: 1.5rem 2rem;
          border-radius: 16px;
          border: 1px solid rgba(124, 77, 255, 0.2);
          max-width: 500px;
          text-align: left;
        }

        .privacy-icon {
          font-size: 2rem;
          flex-shrink: 0;
        }

        .faq-privacy-note h4 {
          color: var(--color-text-primary);
          font-size: 1.1rem;
          font-weight: 700;
          margin: 0 0 0.5rem 0;
        }

        .faq-privacy-note p {
          color: var(--color-text-secondary);
          margin: 0;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        /* Responsive adjustments */
        @media (max-width: 1024px) {
          .enhanced-section-title {
            font-size: 2.25rem;
          }
          
          .enhanced-feature-grid {
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          }
          
          .enhanced-steps-container {
            flex-direction: column;
            gap: 3rem;
          }
          
          .enhanced-step-connector {
            transform: rotate(90deg);
            margin: -1rem 0;
          }
          
          .enhanced-step-desc {
            max-width: 300px;
          }
        }

        @media (max-width: 768px) {
          .enhanced-section-title {
            font-size: 2rem;
          }
          
          .enhanced-feature-grid {
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 2rem;
          }
          
          .enhanced-feature-card {
            padding: 2rem;
          }
          
          .enhanced-workflow-section,
          .enhanced-features-section,
          .faq-section {
            padding: 4rem 0;
          }

          .faq-question {
            padding: 1.25rem 1.5rem;
          }

          .faq-answer-content {
            padding: 0 1.5rem 1.25rem 1.5rem;
          }

          .faq-privacy-note {
            flex-direction: column;
            text-align: center;
            padding: 1.25rem 1.5rem;
          }
        }

        @media (max-width: 480px) {
          .enhanced-section-title {
            font-size: 1.75rem;
            margin-bottom: 3rem;
          }
          
          .enhanced-feature-card {
            padding: 1.5rem;
          }
          
          .enhanced-feature-icon {
            width: 60px;
            height: 60px;
          }
          
          .enhanced-feature-icon svg {
            width: 30px;
            height: 30px;
          }
          
          .enhanced-step-number {
            width: 50px;
            height: 50px;
            font-size: 1.25rem;
          }
          
          .enhanced-step-number-container {
            width: 50px;
            height: 50px;
          }
          
          .enhanced-feature-title {
            font-size: 1.25rem;
          }
          
          .enhanced-workflow-section,
          .enhanced-features-section,
          .faq-section {
            padding: 3rem 0;
          }

          .faq-question {
            padding: 1rem 1.25rem;
          }

          .faq-question-text {
            font-size: 1rem;
          }

          .faq-answer-content {
            padding: 0 1.25rem 1rem 1.25rem;
          }

          .faq-subtitle {
            font-size: 1rem;
            margin-bottom: 2rem;
          }
        }
        
        /* Return to Top Button Styles */
        .return-to-top {
          position: fixed;
          bottom: 2rem;
          right: 2rem;
          width: 3.5rem;
          height: 3.5rem;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(124, 77, 255, 0.9) 0%, rgba(0, 204, 184, 0.9) 100%);
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 
            0 4px 15px rgba(0, 0, 0, 0.3),
            0 0 10px rgba(124, 77, 255, 0.4);
          transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          opacity: 0;
          transform: translateY(20px) scale(0.9);
          pointer-events: none;
          z-index: 100;
        }
        
        .return-to-top.visible {
          opacity: 1;
          transform: translateY(0) scale(1);
          pointer-events: all;
        }
        
        .return-to-top:hover {
          transform: translateY(-5px);
          box-shadow: 
            0 6px 20px rgba(0, 0, 0, 0.4),
            0 0 15px rgba(124, 77, 255, 0.5);
        }
        
        .return-to-top:active {
          transform: translateY(0) scale(0.95);
        }
        
        .return-to-top::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%);
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        
        .return-to-top:hover::before {
          opacity: 1;
        }
        
        .return-to-top svg {
          width: 1.5rem;
          height: 1.5rem;
          stroke-width: 2.5;
        }
        
        /* Enhanced Use Cases Section Styles */
        .enhanced-use-cases-section {
          background-color: rgba(25, 25, 25, 0.95);
          position: relative;
          padding: 6rem 0;
          overflow: hidden;
          box-shadow: 0 -1px 0 rgba(255, 255, 255, 0.05);
        }
        
        .enhanced-use-cases-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, 
            transparent, 
            rgba(124, 77, 255, 0.1), 
            rgba(0, 204, 184, 0.1), 
            transparent
          );
        }
        
        .enhanced-use-cases-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
          position: relative;
          z-index: 2;
        }
        
        .enhanced-use-case-card {
          background: linear-gradient(145deg, rgba(35, 35, 35, 0.4), rgba(25, 25, 25, 0.8));
          border-radius: 16px;
          padding: 2rem;
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.05);
          box-shadow: 
            0 10px 20px rgba(0, 0, 0, 0.1),
            0 6px 6px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          min-height: 220px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        
        .enhanced-use-case-card:hover {
          transform: translateY(-8px);
          box-shadow: 
            0 15px 30px rgba(0, 0, 0, 0.2),
            0 10px 10px rgba(0, 0, 0, 0.1);
          border-color: rgba(255, 255, 255, 0.1);
        }
        
        .enhanced-use-case-card:hover .enhanced-icon-glow {
          opacity: 0.8;
        }
        
        .enhanced-use-case-card:hover .enhanced-use-case-icon {
          transform: scale(1.1);
        }
        
        .enhanced-use-case-card:hover .enhanced-card-shine {
          opacity: 1;
        }
        
        .enhanced-use-case-icon {
          width: 60px;
          height: 60px;
          border-radius: 16px;
          background: linear-gradient(145deg, rgba(45, 45, 45, 0.6), rgba(35, 35, 35, 0.8));
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.25rem;
          position: relative;
          transition: all 0.3s ease;
          box-shadow: 0 8px 15px rgba(0, 0, 0, 0.15);
        }
        
        .enhanced-use-case-icon svg {
          width: 30px;
          height: 30px;
          color: var(--color-primary);
          transition: all 0.3s ease;
          position: relative;
          z-index: 1;
        }
        
        .enhanced-use-case-card:hover .enhanced-use-case-icon svg {
          color: var(--color-secondary);
        }
        
        .enhanced-use-case-title {
          font-size: 1.25rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: var(--color-text-primary);
          position: relative;
        }
        
        .enhanced-use-case-title::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 40px;
          height: 2px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
          border-radius: 2px;
        }
        
        .enhanced-use-case-desc {
          color: var(--color-text-secondary);
          line-height: 1.6;
          margin-top: 1rem;
          font-size: 0.95rem;
        }
        
        .enhanced-use-cases-bg-gradient {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(800px circle at 20% 30%, rgba(124, 77, 255, 0.07) 0%, transparent 60%),
            radial-gradient(600px circle at 80% 70%, rgba(0, 204, 184, 0.07) 0%, transparent 60%);
          z-index: 1;
        }
        
        @media (max-width: 768px) {
          .return-to-top {
            bottom: 1.5rem;
            right: 1.5rem;
            width: 3rem;
            height: 3rem;
          }
          
          .return-to-top svg {
            width: 1.25rem;
            height: 1.25rem;
          }
          
          .enhanced-cta-content {
            padding: 2rem 1.5rem;
          }
          
          .enhanced-cta-title {
            font-size: 2rem;
          }
          
          .enhanced-cta-desc {
            font-size: 1.1rem;
          }
          
          .enhanced-footer-content {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }
          
          .enhanced-footer-links {
            justify-content: center;
          }
          
          .enhanced-footer-bottom {
            flex-direction: column;
            text-align: center;
          }
          
          .enhanced-social-links {
            margin-top: 1rem;
          }

          .enhanced-use-cases-section {
            padding: 4rem 0;
          }
          
          .enhanced-use-cases-grid {
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            gap: 1.5rem;
          }
        }
        
        @media (max-width: 480px) {
          .return-to-top {
            bottom: 1rem;
            right: 1rem;
            width: 2.75rem;
            height: 2.75rem;
          }
          
          .enhanced-cta-title {
            font-size: 1.75rem;
          }
          
          .enhanced-cta-desc {
            font-size: 1rem;
            margin-bottom: 2rem;
          }
          
          .enhanced-cta-button {
            padding: 0.85rem 1.75rem;
            font-size: 1rem;
          }
          
          .enhanced-footer {
            padding: 3rem 0 1.5rem;
          }
          
          .enhanced-footer-links {
            gap: 1.5rem;
          }

          .enhanced-use-cases-section {
            padding: 3rem 0;
          }
          
          .enhanced-use-cases-grid {
            grid-template-columns: 1fr;
            max-width: 300px;
            margin: 0 auto;
          }
          
          .enhanced-use-case-card {
            min-height: auto;
            padding: 1.5rem;
          }
        }
        
        /* Enhanced CTA Section Styles */
        .enhanced-cta-section {
          padding: 6rem 0;
          background-color: var(--color-background);
          position: relative;
          overflow: hidden;
          text-align: center;
        }
        
        .enhanced-cta-content {
          position: relative;
          z-index: 2;
          max-width: 800px;
          margin: 0 auto;
          padding: 3rem;
          background: linear-gradient(145deg, rgba(30, 30, 30, 0.6), rgba(20, 20, 20, 0.8));
          border-radius: 20px;
          box-shadow: 
            0 20px 40px rgba(0, 0, 0, 0.2),
            0 0 0 1px rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .enhanced-cta-title {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 1.5rem;
          color: var(--color-text-primary);
        }
        
        .enhanced-cta-desc {
          font-size: 1.2rem;
          color: var(--color-text-secondary);
          margin-bottom: 2.5rem;
          line-height: 1.6;
        }
        
        .enhanced-cta-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 1rem 2rem;
          font-size: 1.1rem;
          font-weight: 600;
          color: white;
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
          border-radius: 5px;
          box-shadow: 
            0 10px 25px rgba(0, 0, 0, 0.2), 
            0 0 15px rgba(124, 77, 255, 0.4);
          transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          position: relative;
          overflow: hidden;
          border: none;
          cursor: pointer;
          text-decoration: none;
        }
        
        .enhanced-cta-button:hover {
          transform: translateY(-5px);
          box-shadow: 
            0 15px 30px rgba(0, 0, 0, 0.3), 
            0 0 20px rgba(124, 77, 255, 0.5);
            color: white;
        }
        
        .enhanced-cta-button:active {
          transform: translateY(0) scale(0.98);
        }
        
        .enhanced-cta-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.2) 0%, 
            rgba(255, 255, 255, 0) 50%, 
            rgba(255, 255, 255, 0.1) 100%);
          transition: opacity 0.3s ease;
        }
        
        .enhanced-cta-button:hover::before {
          opacity: 0.8;
        }
        
        .button-arrow {
          width: 18px;
          height: 18px;
          transition: transform 0.3s ease;
        }
        
        .enhanced-cta-button:hover .button-arrow {
          transform: translateX(4px);
        }
        
        .enhanced-cta-bg-gradient {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(800px circle at 70% 20%, rgba(124, 77, 255, 0.1) 0%, transparent 60%),
            radial-gradient(600px circle at 30% 70%, rgba(0, 204, 184, 0.1) 0%, transparent 60%);
          z-index: 1;
        }
        
        /* Enhanced Footer Styles */
        .enhanced-footer {
          background-color: var(--color-background-tertiary);
          padding: 4rem 0 2rem;
          position: relative;
          overflow: hidden;
        }
        
        .enhanced-footer::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, 
            transparent, 
            rgba(124, 77, 255, 0.3), 
            rgba(0, 204, 184, 0.3), 
            transparent
          );
        }
        
        .enhanced-footer-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1.5rem;
        }
        
        .enhanced-footer-logo {
          font-size: 1.5rem;
          font-weight: 700;
        }
        
        .enhanced-footer-links {
          display: flex;
          gap: 2rem;
          flex-wrap: wrap;
        }
        
        .enhanced-footer-link {
          color: var(--color-text-secondary);
          transition: all 0.3s ease;
          position: relative;
          padding: 0.25rem 0;
        }
        
        .enhanced-footer-link::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 1px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s ease;
        }
        
        .enhanced-footer-link:hover {
          color: var(--color-text-primary);
        }
        
        .enhanced-footer-link:hover::after {
          transform: scaleX(1);
        }
        
        .enhanced-footer-divider {
          width: 100%;
          height: 1px;
          background: linear-gradient(90deg, 
            transparent, 
            rgba(255, 255, 255, 0.05), 
            transparent
          );
          margin-bottom: 1.5rem;
        }
        
        .enhanced-footer-bottom {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }
        
        .enhanced-copyright {
          color: var(--color-text-tertiary);
          font-size: 0.875rem;
        }
        
        .enhanced-social-links {
          display: flex;
          gap: 1rem;
        }
        
        .enhanced-social-link {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.05);
          color: var(--color-text-secondary);
          transition: all 0.3s ease;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .enhanced-social-link:hover {
          background-color: rgba(124, 77, 255, 0.1);
          color: var(--color-text-primary);
          border-color: rgba(124, 77, 255, 0.3);
          transform: translateY(-3px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
        }
        
        .enhanced-social-link svg {
          width: 20px;
          height: 20px;
        }

        /* NFC Section Styles */
  .nfc-section {
    background: linear-gradient(135deg, rgba(20, 20, 40, 0.95) 0%, rgba(30, 30, 50, 0.95) 50%, rgba(25, 25, 45, 0.95) 100%);
    padding: 6rem 0;
    position: relative;
    overflow: hidden;
    border-top: 1px solid rgba(124, 77, 255, 0.2);
    border-bottom: 1px solid rgba(124, 77, 255, 0.2);
  }

  .nfc-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
    position: relative;
    z-index: 2;
  }

  .nfc-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: linear-gradient(135deg, rgba(124, 77, 255, 0.15) 0%, rgba(0, 204, 184, 0.15) 100%);
    border: 1px solid rgba(124, 77, 255, 0.3);
    padding: 0.5rem 1rem;
    border-radius: 50px;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--color-primary);
    margin-bottom: 1.5rem;
    backdrop-filter: blur(10px);
  }

  .nfc-badge-icon {
    font-size: 1rem;
    animation: rocket 2s ease-in-out infinite;
  }

  @keyframes rocket {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-3px); }
  }

  .nfc-title {
    font-size: 2.5rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    color: var(--color-text-primary);
    line-height: 1.2;
  }

  .nfc-subtitle {
    font-size: 1.2rem;
    color: var(--color-text-secondary);
    line-height: 1.6;
    margin-bottom: 2.5rem;
  }

  .nfc-features {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 2.5rem;
  }

  .nfc-feature-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    transition: all 0.3s ease;
  }

  .nfc-feature-item:hover {
    background: rgba(124, 77, 255, 0.05);
    border-color: rgba(124, 77, 255, 0.2);
    transform: translateY(-2px);
  }

  .nfc-feature-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(124, 77, 255, 0.2) 0%, rgba(0, 204, 184, 0.2) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .nfc-feature-icon svg {
    width: 20px;
    height: 20px;
    color: var(--color-primary);
  }

  .nfc-feature-text h4 {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0 0 0.5rem 0;
  }

  .nfc-feature-text p {
    font-size: 0.95rem;
    color: var(--color-text-secondary);
    margin: 0;
    line-height: 1.5;
  }

  .nfc-cta {
    text-align: left;
  }

  /* NFC Waitlist Form */
  .nfc-waitlist-form {
    margin-bottom: 1rem;
  }

  .nfc-email-input-container {
    display: flex;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .nfc-email-input {
    flex: 1;
    padding: 1rem 1.25rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    color: var(--color-text-primary);
    font-size: 1rem;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
  }

  .nfc-email-input:focus {
    outline: none;
    border-color: rgba(124, 77, 255, 0.5);
    box-shadow: 0 0 0 2px rgba(124, 77, 255, 0.1);
    background: rgba(255, 255, 255, 0.08);
  }

  .nfc-email-input::placeholder {
    color: var(--color-text-tertiary);
  }

  .nfc-email-input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .nfc-notify-button {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 2rem;
    background: linear-gradient(135deg, rgba(124, 77, 255, 0.2) 0%, rgba(0, 204, 184, 0.2) 100%);
    border: 1px solid rgba(124, 77, 255, 0.4);
    border-radius: 8px;
    color: white;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    white-space: nowrap;
    min-width: 140px;
    justify-content: center;
  }

  .nfc-notify-button:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(124, 77, 255, 0.3) 0%, rgba(0, 204, 184, 0.3) 100%);
    border-color: rgba(124, 77, 255, 0.6);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(124, 77, 255, 0.2);
  }

  .nfc-notify-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .nfc-loading-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(124, 77, 255, 0.3);
    border-top: 2px solid var(--color-primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .nfc-success-icon {
    color: #00ff88;
    font-weight: bold;
    font-size: 1.1rem;
  }

  .nfc-error-message {
    color: #ff4444;
    font-size: 0.875rem;
    margin: 0;
    padding: 0.5rem 0;
    text-align: center;
  }

  .nfc-success-message {
    color: #00ff88;
    font-size: 0.875rem;
    margin: 0;
    padding: 0.5rem 0;
    text-align: center;
    font-weight: 500;
  }

  .nfc-notify-icon {
    font-size: 1.2rem;
    animation: bell 3s ease-in-out infinite;
  }

  @keyframes bell {
    0%, 90%, 100% { transform: rotate(0deg); }
    5%, 15% { transform: rotate(15deg); }
    10% { transform: rotate(-15deg); }
  }

  .nfc-note {
    font-size: 0.875rem;
    color: var(--color-text-tertiary);
    margin-top: 1rem;
    margin-bottom: 0;
  }

  /* NFC Visual Content */
  .nfc-visual-content {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .nfc-card-showcase {
    position: relative;
    perspective: 1000px;
  }

  .nfc-card {
    width: 300px;
    height: 190px;
    position: relative;
    animation: float 6s ease-in-out infinite;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px) rotateY(0deg); }
    25% { transform: translateY(-10px) rotateY(5deg); }
    50% { transform: translateY(0px) rotateY(0deg); }
    75% { transform: translateY(-5px) rotateY(-5deg); }
  }

  .nfc-card-inner {
    width: 100%;
    height: 100%;
    position: relative;
    transform-style: preserve-3d;
  }

  .nfc-card-front {
    position: absolute;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(20, 20, 20, 0.98) 100%);
    border-radius: 16px;
    border: 1px solid rgba(124, 77, 255, 0.3);
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.3),
      0 0 20px rgba(124, 77, 255, 0.2);
    backdrop-filter: blur(20px);
  }

  .nfc-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .nfc-logo {
    font-size: 1rem;
    font-weight: 700;
    background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .nfc-chip {
    width: 32px;
    height: 24px;
    background: linear-gradient(135deg, #c9b037 0%, #f4e06d 100%);
    border-radius: 4px;
    position: relative;
    box-shadow: 0 2px 8px rgba(201, 176, 55, 0.3);
  }

  .nfc-chip-inner {
    position: absolute;
    top: 2px;
    left: 2px;
    right: 2px;
    bottom: 2px;
    background: repeating-linear-gradient(
      45deg,
      rgba(0, 0, 0, 0.1),
      rgba(0, 0, 0, 0.1) 1px,
      transparent 1px,
      transparent 3px
    );
    border-radius: 2px;
  }

  .nfc-card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 1rem;
  }

  .nfc-verification-dots {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
  }

  .nfc-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    animation: pulse 2s ease-in-out infinite;
  }

  .nfc-dot.twitter { 
    background: #1DA1F2; 
    animation-delay: 0s;
  }
  .nfc-dot.github { 
    background: #333; 
    animation-delay: 0.2s;
  }
  .nfc-dot.discord { 
    background: #5865F2; 
    animation-delay: 0.4s;
  }
  .nfc-dot.solana { 
    background: #9945FF; 
    animation-delay: 0.6s;
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.6; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.2); }
  }

  .nfc-card-address {
    text-align: center;
  }

  .nfc-address-label {
    font-size: 0.7rem;
    color: var(--color-text-tertiary);
    margin-bottom: 0.25rem;
  }

  .nfc-address-value {
    font-size: 0.8rem;
    font-family: monospace;
    color: var(--color-text-secondary);
    background: rgba(255, 255, 255, 0.05);
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    display: inline-block;
  }

  .nfc-card-footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }

  .nfc-footer-left {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .nfc-id {
    font-size: 0.7rem;
    color: var(--color-text-tertiary);
  }

  .nfc-category {
    font-size: 0.7rem;
    color: var(--color-primary);
    background: rgba(124, 77, 255, 0.1);
    padding: 0.125rem 0.375rem;
    border-radius: 4px;
    display: inline-block;
  }

  .nfc-footer-right {
    opacity: 0.7;
  }

  .nfc-icon svg {
    width: 16px;
    height: 16px;
    color: var(--color-primary);
  }

  /* NFC Interaction Animation */
  .nfc-interaction-visual {
    position: absolute;
    right: -120px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
  }

  .nfc-tap-animation {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .nfc-hand {
    z-index: 2;
    animation: tap 3s ease-in-out infinite;
  }

  @keyframes tap {
    0%, 70%, 100% { transform: translateY(0) rotate(0deg); }
    10%, 60% { transform: translateY(-10px) rotate(-10deg); }
  }

  .nfc-hand svg {
    color: var(--color-text-secondary);
    filter: drop-shadow(0 0 10px rgba(124, 77, 255, 0.3));
  }

  .nfc-waves {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .nfc-wave {
    position: absolute;
    border: 2px solid var(--color-primary);
    border-radius: 50%;
    opacity: 0;
    animation: wave 3s ease-out infinite;
  }

  .nfc-wave-1 {
    width: 60px;
    height: 60px;
    top: -30px;
    left: -30px;
    animation-delay: 0s;
  }

  .nfc-wave-2 {
    width: 80px;
    height: 80px;
    top: -40px;
    left: -40px;
    animation-delay: 0.5s;
  }

  .nfc-wave-3 {
    width: 100px;
    height: 100px;
    top: -50px;
    left: -50px;
    animation-delay: 1s;
  }

  @keyframes wave {
    0% {
      opacity: 1;
      transform: scale(0.1);
    }
    70% {
      opacity: 0.6;
      transform: scale(1);
    }
    100% {
      opacity: 0;
      transform: scale(1.2);
    }
  }

  .nfc-reader {
    width: 100px;
    height: 60px;
    background: linear-gradient(135deg, rgba(20, 20, 20, 0.95) 0%, rgba(15, 15, 15, 0.98) 100%);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 
      0 4px 15px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
    position: relative;
  }

  .nfc-reader::before {
    content: '';
    position: absolute;
    top: 4px;
    right: 4px;
    width: 6px;
    height: 6px;
    background: #00ff88;
    border-radius: 50%;
    box-shadow: 0 0 8px #00ff88;
    animation: powerLed 2s ease-in-out infinite;
  }

  @keyframes powerLed {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }

  .nfc-reader-screen {
    width: 80px;
    height: 40px;
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.9) 0%, rgba(10, 10, 10, 0.95) 100%);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(0, 255, 136, 0.3);
    position: relative;
    overflow: hidden;
  }

  .nfc-reader-screen::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(0, 255, 136, 0.1), 
      transparent
    );
    animation: screenScan 3s ease-in-out infinite;
  }

  @keyframes screenScan {
    0%, 80%, 100% { left: -100%; }
    10%, 70% { left: 100%; }
  }

  .nfc-status {
    display: flex;
    align-items: center;
    justify-content: center;
    animation: statusBlink 3s ease-in-out infinite;
    z-index: 2;
  }

  @keyframes statusBlink {
    0%, 80%, 100% { opacity: 0.4; }
    10%, 70% { opacity: 1; }
  }

  .nfc-status-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
  }

  .nfc-check-icon {
    color: #00ff88;
    font-size: 1rem;
    font-weight: bold;
    text-shadow: 0 0 8px #00ff88;
  }

  .nfc-status-text {
    color: #00ff88;
    font-size: 0.5rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-family: 'Courier New', monospace;
    text-shadow: 0 0 4px #00ff88;
  }

  .nfc-bg-gradient {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(800px circle at 20% 50%, rgba(124, 77, 255, 0.08) 0%, transparent 60%),
      radial-gradient(600px circle at 80% 50%, rgba(0, 204, 184, 0.08) 0%, transparent 60%);
    z-index: 1;
  }

  /* Responsive Design */
  @media (max-width: 1024px) {
    .nfc-content {
      grid-template-columns: 1fr;
      gap: 3rem;
      text-align: center;
    }

    .nfc-interaction-visual {
      position: static;
      transform: none;
      margin-top: 2rem;
    }

    .nfc-card {
      width: 280px;
      height: 175px;
    }
  }

      @media (max-width: 768px) {
      .nfc-email-input-container {
        flex-direction: column;
        gap: 0.75rem;
      }

      .nfc-notify-button {
        width: 100%;
        min-width: unset;
      }
    .nfc-section {
      padding: 4rem 0;
    }

    .nfc-title {
      font-size: 2rem;
    }

    .nfc-subtitle {
      font-size: 1.1rem;
    }

    .nfc-card {
      width: 250px;
      height: 155px;
    }

    .nfc-card-front {
      padding: 1rem;
    }

    .nfc-interaction-visual {
      display: none;
    }
  }

  @media (max-width: 480px) {
    .nfc-section {
      padding: 3rem 0;
    }

    .nfc-title {
      font-size: 1.75rem;
    }

    .nfc-features {
      gap: 1rem;
    }

    .nfc-feature-item {
      padding: 0.75rem;
    }

    .nfc-notify-button {
      padding: 0.875rem 1.5rem;
      font-size: 0.95rem;
    }

    .nfc-card {
      width: 220px;
      height: 140px;
    }
      `}</style>
    </div>
  );
};

export default LandingPage;